import { useCallback, useState } from 'react';
import type { MessageFeedbackState } from './ChatMessage';

export type SubmitFeedbackFn = (args: {
  source: 'message' | 'general';
  rating?: 'up' | 'down';
  comment?: string;
  category?: string;
  messageId?: string;
  messageIndex?: number;
  messageText?: string;
}) => Promise<{ ok: boolean }>;

/**
 * Per-message feedback state + handlers, shared by ChatWindow and
 * ExperienceBuilderView (both render <ChatMessage> and want the same
 * thumbs/comment behavior). State is keyed by message id.
 *
 * Crash-safe: every handler is wrapped so a feedback click can never
 * break the chat. Optimistic + double-submit guarded. Thumbs-down records
 * the down-vote immediately AND opens the comment box (the comment is an
 * optional follow-up).
 */
export function useMessageFeedback(submitFeedback?: SubmitFeedbackFn) {
  const [feedbackByMsg, setFeedbackByMsg] = useState<Record<string, MessageFeedbackState>>({});

  const reset = useCallback(() => setFeedbackByMsg({}), []);

  const handleRate = useCallback(
    (msgId: string, idx: number, rating: 'up' | 'down', messageText?: string) => {
      try {
        setFeedbackByMsg((s) => {
          const prev = s[msgId];
          if (prev?.submitting) return s; // in-flight guard
          if (prev?.rating === rating) return s; // idempotent re-click
          return {
            ...s,
            [msgId]: {
              ...prev,
              rating,
              submitting: true,
              commentOpen: rating === 'down' ? true : prev?.commentOpen ?? false,
            },
          };
        });
        void submitFeedback?.({ source: 'message', rating, messageId: msgId, messageIndex: idx, messageText })
          .catch(() => undefined)
          .finally(() =>
            setFeedbackByMsg((s) => ({ ...s, [msgId]: { ...s[msgId], submitting: false } }))
          );
      } catch {
        /* never break the chat over a feedback click */
      }
    },
    [submitFeedback]
  );

  const handleToggleComment = useCallback((msgId: string) => {
    setFeedbackByMsg((s) => ({
      ...s,
      [msgId]: { ...s[msgId], commentOpen: !s[msgId]?.commentOpen },
    }));
  }, []);

  const handleSubmitComment = useCallback(
    (msgId: string, idx: number, text: string, messageText?: string) => {
      try {
        const rating = feedbackByMsg[msgId]?.rating;
        void submitFeedback?.({
          source: 'message',
          rating,
          comment: text,
          messageId: msgId,
          messageIndex: idx,
          messageText,
        }).catch(() => undefined);
        setFeedbackByMsg((s) => ({ ...s, [msgId]: { ...s[msgId], commentSubmitted: true } }));
      } catch {
        /* swallow */
      }
    },
    [submitFeedback, feedbackByMsg]
  );

  return { feedbackByMsg, handleRate, handleToggleComment, handleSubmitComment, reset };
}
